'use client';

import { CustomEventName, GTMConstants, GTMEventKey, sendGTM } from '@fe-cnyes/fe-common-gtm';
import ReactSlick from '@fe-common-ui/Slick';
import { SCREEN_SIZE } from '@fe-common-utils/constants/screen';
import NewsTypeBadge from '@fe-news/components/molecules/NewsTypeBadge/NewsTypeBadge';
import useFetchFeatureReport from '@fe-news/components/Aside/NewsFeatureReport/hooks/use-fetch-feature-report';
import { styled } from '@linaria/react';
import Image from 'next/image';
import Link from 'next/link';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import DotsWrapper from './DotsWrapper';

// ============================================================================
// 常數定義
// ============================================================================

/** 專題報導插入位置：在第幾個 index 之後插入（0-indexed）
 * [0, 1] 表示在新聞1後、新聞2後插入，使專題報導出現在第 2、4 則位置
 * 結果順序：新聞1 → 專題1 → 新聞2 → 專題2 → 新聞3 → 新聞4 → 新聞5
 */
const FEATURE_REPORT_INSERT_POSITIONS = new Set([0, 1]);

// ============================================================================
// 樣式元件
// ============================================================================

const StyledSlider = styled.div`
  position: relative;
  overflow-x: hidden;
  background-color: #fff;
  padding-bottom: 27px;
  margin-top: 24px;

  img {
    border-radius: 10px;
  }
`;

const SlideWrapper = styled.div`
  .slide {
    padding: 0 8px;
  }

  .image-container,
  .feature-report-container {
    display: block;
    height: 200px;
    position: relative;
    border-radius: 10px;
    overflow: hidden;

    img {
      object-fit: cover;
    }

    @media only screen and (min-width: ${SCREEN_SIZE.MOBILE}px) {
      height: 400px;
    }
  }
`;

const Mask = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: linear-gradient(to bottom, rgba(49, 62, 79, 0), rgba(22, 29, 39, 0.6));
  border-radius: 10px;
`;

const Title = styled.div`
  position: absolute;
  bottom: 12px;
  left: 12px;
  right: 12px;
  width: calc(100% - 24px);
  text-shadow: 0 0 6px rgba(0, 0, 0, 0.5);
  font-size: 20px;
  font-weight: 500;
  line-height: 30px;
  letter-spacing: 0.8px;
  color: #fff;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ArrowTouch = styled.button`
  display: block;
  position: absolute;
  top: 0;
  width: 10%;
  height: 100%;
  background-color: transparent;
  z-index: 2;
`;

// ============================================================================
// 類型定義
// ============================================================================

/** 輪播項目類型 */
type CarouselItem = { type: 'news'; data: News; index: number } | { type: 'feature-report'; data: FeatureReportType };

// ============================================================================
// GTM 事件處理
// ============================================================================

const sendNewsClickGTM = (newsId: number, title: string) => {
  sendGTM({
    [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_NEWS,
    [GTMEventKey.SECTION]: `${GTMConstants.SECTION_PLACEHOLDER}_精選新聞`,
    [GTMEventKey.CLICK_ITEM]: `${newsId}_${title}`
  });
};

const sendFeatureReportClickGTM = (id: number, title: string) => {
  sendGTM({
    [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_NEWS,
    [GTMEventKey.SECTION]: `${GTMConstants.SECTION_PLACEHOLDER}_專題報導`,
    [GTMEventKey.CLICK_ITEM]: `${id}_${title}`
  });
};

// ============================================================================
// 主元件
// ============================================================================

interface NavigatorCarouselProps {
  news: News[];
}

const NavigatorCarousel: FC<NavigatorCarouselProps> = ({ news }) => {
  const [scrollIndex, setScrollIndex] = useState(0);
  const sliderReference = useRef<{ slickNext: () => void; slickPrev: () => void } | null>(null);

  // 取得專題報導
  const { data: featureReports } = useFetchFeatureReport();

  // 建立輪播項目列表：將專題報導穿插於新聞中
  const carouselItems = useMemo<CarouselItem[]>(() => {
    if (!news?.length) return [];

    // 過濾掉無效的項目
    const validNews = news.filter(Boolean) as News[];

    const items: CarouselItem[] = [];
    let featureReportIndex = 0;

    validNews.forEach((newsItem, index) => {
      // 加入新聞項目
      items.push({ type: 'news', data: newsItem, index });

      // 在指定位置插入專題報導（第 2、4 則位置，即 index 1, 3 之後）
      if (FEATURE_REPORT_INSERT_POSITIONS.has(index) && featureReports?.[featureReportIndex]) {
        items.push({ type: 'feature-report', data: featureReports[featureReportIndex] });
        featureReportIndex++;
      }
    });

    return items;
  }, [news, featureReports]);

  // 輪播設定
  const settings = useMemo(
    () => ({
      dots: false,
      className: 'center',
      centerMode: true,
      infinite: true,
      centerPadding: '24px',
      slidesToShow: 1,
      speed: 500,
      autoplay: false,
      autoplaySpeed: 4000,
      beforeChange: (_: number, newIndex: number) => setScrollIndex(newIndex)
    }),
    []
  );

  // 手動切換 slide
  const onSlideChange = useCallback(
    (direction: 'prev' | 'next') => () => {
      if (direction === 'next') {
        sliderReference.current?.slickNext();
      } else {
        sliderReference.current?.slickPrev();
      }
    },
    []
  );

  // 新聞點擊事件
  const onNewsClick = useCallback(
    (newsId: number, title: string) => () => {
      sendNewsClickGTM(newsId, title);
    },
    []
  );

  // 專題報導點擊事件
  const onFeatureReportClick = useCallback(
    (id: number, title: string) => () => {
      sendFeatureReportClickGTM(id, title);
    },
    []
  );

  if (!news?.length) return null;

  return (
    <StyledSlider>
      <ArrowTouch style={{ left: 0 }} onClick={onSlideChange('prev')} />
      <ReactSlick ref={sliderReference} {...settings}>
        {carouselItems.map(item => {
          switch (item.type) {
            case 'news': {
              const { coverSrc, title, newsId, is24h, keyword } = item.data;
              const href = `/news/id/${newsId}`;
              const imageSource = coverSrc?.l?.src || `/assets/images/news_default_images/${newsId % 10}.webp`;

              return (
                <SlideWrapper key={`news-${newsId}`}>
                  <div className='slide'>
                    <Link className='image-container' prefetch={false} href={href} onClick={onNewsClick(newsId, title)}>
                      <Image priority={item.index === 0} alt='news-cover' src={imageSource} fill unoptimized />
                      <Mask />
                      <Title>
                        <NewsTypeBadge is24h={Boolean(is24h)} isMobile keyword={keyword} />
                        {title}
                      </Title>
                    </Link>
                  </div>
                </SlideWrapper>
              );
            }

            case 'feature-report': {
              const { id, title, link, cover } = item.data;

              return (
                <SlideWrapper key={`feature-report-${id}`}>
                  <div className='slide'>
                    <Link
                      className='feature-report-container'
                      prefetch={false}
                      href={link}
                      onClick={onFeatureReportClick(id, title)}
                    >
                      <Image alt='feature-report-cover' src={cover} fill unoptimized />
                      <Mask />
                      <Title>{title}</Title>
                    </Link>
                  </div>
                </SlideWrapper>
              );
            }

            default:
              return null;
          }
        })}
      </ReactSlick>
      <DotsWrapper newsNumber={carouselItems.length} scrollIndex={scrollIndex} />
      <ArrowTouch style={{ right: 0 }} onClick={onSlideChange('next')} />
    </StyledSlider>
  );
};

export default NavigatorCarousel;
